// Draws a shareable "Name Card" onto a canvas — used for the social/friend
// share feature on the expanded name row. Kept dependency-free (no
// html2canvas) since the card layout is simple enough to paint directly.

export const CARD_WIDTH = 1080;
// Not a fixed height: the card grows taller than this when a long meaning
// needs more room, so the text never gets cut off or crowded against the
// footer. This is just the floor for short entries.
const MIN_CARD_HEIGHT = 1420;

const GENDER_COLORS = {
  Boy: { bg: '#dbeafe', text: '#2563eb' },
  Girl: { bg: '#fce7f3', text: '#db2777' },
  Unisex: { bg: '#dcfce7', text: '#059669' },
};

const CATEGORY_COLORS = {
  religious: { bg: '#fef9c3', text: '#b45309' },
  historical: { bg: '#e0e7ff', text: '#3730a3' },
  modern: { bg: '#d1fae5', text: '#047857' },
};
const DEFAULT_CATEGORY_COLOR = { bg: '#f3f4f6', text: '#7c3aed' };

const SKYLINE_THEMES = {
  pink: {
    sun: '#fde2f0',
    hillBack: '#f6d7ea',
    hillFront: '#f0c3df',
    ziggurat: '#e79cc7',
    building: '#eeb3d6',
    palm: '#d88bbf',
  },
  blue: {
    sun: '#dbeeff',
    hillBack: '#d9e8fb',
    hillFront: '#c2dbf7',
    ziggurat: '#7fb2e8',
    building: '#a8cdf2',
    palm: '#5f95d9',
  },
};

function getSkylineTheme(genderLabel) {
  return genderLabel === 'Boy' ? SKYLINE_THEMES.blue : SKYLINE_THEMES.pink;
}

const SYRIAC_FONTS = {
  E: 'Noto Sans Syriac Eastern',
  W: 'Noto Sans Syriac Western',
};

const NAME_FONT = 'Playfair Display';
const SANS_FONT = 'Segoe UI';

async function loadFonts(syriacFontType) {
  if (!document.fonts) return;
  const syriacFont = SYRIAC_FONTS[syriacFontType] || SYRIAC_FONTS.E;
  try {
    await Promise.all([
      document.fonts.load(`800 96px "${NAME_FONT}"`),
      document.fonts.load(`600 132px "${syriacFont}"`),
      document.fonts.load(`700 32px "${SANS_FONT}"`),
      document.fonts.load(`600 118px "${SANS_FONT}"`),
      document.fonts.load(`400 34px "${SANS_FONT}"`),
      document.fonts.ready,
    ]);
  } catch {
    // Fall back to default fonts if a face fails to load.
  }
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundedRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Wraps text within maxWidth (using the currently-set ctx.font) into at most
// maxLines lines, ellipsizing the last line if it doesn't all fit. Pure
// measurement — does not draw anything, so it can be used up-front to size
// the card before anything is painted.
function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = (text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && current) lines.push(current);

  if (lines.length === maxLines && words.length && lines.join(' ').length < text.length) {
    let last = lines[maxLines - 1];
    while (ctx.measureText(`${last}…`).width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = `${last}…`;
  }

  return lines;
}

// Draws pre-wrapped, centered lines starting at (centerX, startY). Returns
// the y position right after the last line.
function drawLines(ctx, lines, centerX, startY, lineHeight) {
  lines.forEach((line, i) => {
    ctx.fillText(line, centerX, startY + i * lineHeight);
  });
  return startY + lines.length * lineHeight;
}

// Shrinks the font size (down to minSize) until `text` fits within maxWidth.
function fitFontSize(ctx, text, fontTemplate, startSize, minSize, maxWidth) {
  let size = startSize;
  while (size > minSize) {
    ctx.font = fontTemplate(size);
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 4;
  }
  return size;
}

function measureSpacedWidth(ctx, text, letterSpacing) {
  return (
    text.split('').reduce((sum, ch) => sum + ctx.measureText(ch).width, 0) +
    letterSpacing * Math.max(0, text.length - 1)
  );
}

// Draws left-to-right text with manual letter-spacing, centered at centerX.
// (Avoids relying on ctx.letterSpacing, which isn't supported everywhere.)
function fillTextSpaced(ctx, text, centerX, y, letterSpacing) {
  const chars = text.split('');
  const widths = chars.map((ch) => ctx.measureText(ch).width);
  const totalWidth = widths.reduce((a, b) => a + b, 0) + letterSpacing * Math.max(0, chars.length - 1);
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  let x = centerX - totalWidth / 2;
  chars.forEach((ch, i) => {
    ctx.fillText(ch, x, y);
    x += widths[i] + letterSpacing;
  });
  ctx.textAlign = prevAlign;
}

// Centers a row of rounded "pill" labels and returns the y position below them.
function drawPillRow(ctx, items, centerX, y, opts = {}) {
  if (!items.length) return y;
  const {
    height = 62,
    gap = 18,
    paddingX = 34,
    letterSpacing = 2,
    font = `700 26px "${SANS_FONT}", Arial, sans-serif`,
  } = opts;
  ctx.font = font;
  ctx.textBaseline = 'middle';
  const widths = items.map((item) => measureSpacedWidth(ctx, item.label, letterSpacing) + paddingX * 2);
  const totalWidth = widths.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
  let x = centerX - totalWidth / 2;

  items.forEach((item, i) => {
    const w = widths[i];
    ctx.fillStyle = item.bg;
    roundedRectPath(ctx, x, y, w, height, height / 2);
    ctx.fill();

    ctx.fillStyle = item.text;
    fillTextSpaced(ctx, item.label.toUpperCase(), x + w / 2, y + height / 2 + 1, letterSpacing);

    x += w + gap;
  });

  ctx.textBaseline = 'alphabetic';
  return y + height;
}

// A small three-petal leaf flourish, used to break up the divider line.
function drawLeafOrnament(ctx, cx, cy, size, color) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.fillStyle = color;
  [-32, 0, 32].forEach((angle) => {
    ctx.save();
    ctx.rotate((angle * Math.PI) / 180);
    ctx.beginPath();
    ctx.moveTo(0, 4);
    ctx.quadraticCurveTo(size * 0.42, -size * 0.4, 0, -size);
    ctx.quadraticCurveTo(-size * 0.42, -size * 0.4, 0, 4);
    ctx.fill();
    ctx.restore();
  });
  ctx.restore();
}

// A single drooping palm frond, stroked outward from (x0, y0) at angleDeg
// from vertical (negative = leaning left).
function drawPalmFrond(ctx, x0, y0, angleDeg, length, color) {
  const rad = (angleDeg * Math.PI) / 180;
  const midX = x0 + Math.sin(rad) * length * 0.55;
  const midY = y0 - Math.cos(rad) * length * 0.85;
  const endX = x0 + Math.sin(rad) * length;
  const endY = y0 - Math.cos(rad) * length * 0.35;
  ctx.strokeStyle = color;
  ctx.lineWidth = 9;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.quadraticCurveTo(midX, midY, endX, endY);
  ctx.stroke();
}

function drawPalmTree(ctx, x, baseY, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 10;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, baseY);
  ctx.quadraticCurveTo(x + 20, baseY - 90, x + 6, baseY - 150);
  ctx.stroke();

  const crownX = x + 6;
  const crownY = baseY - 150;
  [-58, -30, -8, 14, 38, 62].forEach((angle, i) => {
    drawPalmFrond(ctx, crownX, crownY, angle, i % 2 === 0 ? 78 : 64, color);
  });
}

// A soft, stylized Mesopotamian skyline (hills, sun, a ziggurat and a palm
// tree) anchored to the bottom of the card. Purely decorative.
function drawSkyline(ctx, panelX, panelY, panelW, panelH, theme) {
  const bandBottom = panelY + panelH;
  const bandTop = bandBottom - 220;

  ctx.save();
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 40);
  ctx.clip();

  // Sun, tucked behind the crest of the hills.
  ctx.fillStyle = theme.sun;
  ctx.beginPath();
  ctx.arc(panelX + panelW * 0.62, bandTop + 26, 58, 0, Math.PI * 2);
  ctx.fill();

  // Back hill.
  ctx.fillStyle = theme.hillBack;
  ctx.beginPath();
  ctx.moveTo(panelX, bandBottom);
  ctx.lineTo(panelX, bandTop + 130);
  ctx.quadraticCurveTo(panelX + panelW * 0.25, bandTop + 50, panelX + panelW * 0.5, bandTop + 100);
  ctx.quadraticCurveTo(panelX + panelW * 0.75, bandTop + 150, panelX + panelW, bandTop + 80);
  ctx.lineTo(panelX + panelW, bandBottom);
  ctx.closePath();
  ctx.fill();

  // Ziggurat, kept clear of the front hill's crest (which peaks further right).
  const zigX = panelX + panelW * 0.2;
  const zigBaseY = bandBottom - 24;
  const tierHeight = 22;
  const baseWidth = 130;
  ctx.fillStyle = theme.ziggurat;
  for (let tier = 0; tier < 4; tier += 1) {
    const w = baseWidth - tier * 26;
    const tierY = zigBaseY - (tier + 1) * tierHeight;
    ctx.fillRect(zigX - w / 2, tierY, w, tierHeight - 4);
  }

  // Small building beside the ziggurat, with a cut-out doorway.
  const bldgX = zigX + 96;
  const bldgW = 44;
  const bldgH = 80;
  ctx.fillStyle = theme.building;
  ctx.fillRect(bldgX, bandBottom - bldgH - 14, bldgW, bldgH);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(bldgX + bldgW / 2 - 8, bandBottom - 34, 16, 20);

  // Palm tree, well clear of the sun.
  drawPalmTree(ctx, panelX + panelW - 116, bandBottom - 8, theme.palm);

  // Front hill, drawn last so it partially covers the buildings for depth
  // while leaving the ziggurat and palm tree standing above the crest.
  ctx.fillStyle = theme.hillFront;
  ctx.beginPath();
  ctx.moveTo(panelX, bandBottom);
  ctx.lineTo(panelX, bandBottom - 50);
  ctx.quadraticCurveTo(panelX + panelW * 0.4, bandBottom - 90, panelX + panelW * 0.62, bandBottom - 130);
  ctx.quadraticCurveTo(panelX + panelW * 0.82, bandBottom - 60, panelX + panelW, bandBottom - 40);
  ctx.lineTo(panelX + panelW, bandBottom);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

// Fixed vertical rhythm shared between the layout pass (which sizes the
// card) and the draw pass (which paints it), so the two can never drift
// apart. Only nameSize and the meaning's line count vary per name.
const HEADER_TOP = 78;
const LOGO_HEIGHT = 56;
const LOGO_GAP = 90;
const PILL_HEIGHT = 62;
const PILL_GAP = 170;
const NAME_GAP = 46;
const SYRIAC_ADVANCE = 176;
const ARABIC_ADVANCE = 148;
const DIVIDER_GAP_BEFORE = 46;
const DIVIDER_GAP_AFTER = 78;
const MEANING_LINE_HEIGHT = 52;
const MEANING_MAX_LINES = 8;
const FOOTER_GAP = 56;
const FOOTER_TO_BOTTOM = 210;

export async function drawNameCard(canvas, { name, getGenderLabel, syriacFontType }) {
  await loadFonts(syriacFontType);
  const ctx = canvas.getContext('2d');
  const centerX = CARD_WIDTH / 2;
  const margin = 48;
  const panelW = CARD_WIDTH - margin * 2;

  const genderLabel = getGenderLabel(name.gender);
  const engName = (name.eng_word || '').trim();
  const genderColor = GENDER_COLORS[genderLabel] || DEFAULT_CATEGORY_COLOR;
  const pills = [{ label: genderLabel, ...genderColor }];
  if (Array.isArray(name.category)) {
    name.category.forEach((c) => {
      const color = CATEGORY_COLORS[c?.toLowerCase()] || DEFAULT_CATEGORY_COLOR;
      pills.push({ label: c.charAt(0).toUpperCase() + c.slice(1), ...color });
    });
  }

  const logo = await loadImage('/bk-logo.png');

  const nameFontTemplate = (size) => `800 ${size}px "${NAME_FONT}", Georgia, serif`;
  const nameSize = fitFontSize(ctx, engName, nameFontTemplate, 190, 72, panelW - 160);

  const meaningFont = `400 42px "${SANS_FONT}", Arial, sans-serif`;
  ctx.font = meaningFont;
  const meaningLines = name.meaning ? wrapLines(ctx, name.meaning, panelW - 200, MEANING_MAX_LINES) : [];

  // ---- Size the card to fit its content, then paint. ----
  let y = HEADER_TOP;
  if (logo) y += LOGO_HEIGHT + LOGO_GAP;
  y += PILL_HEIGHT + PILL_GAP;
  y += nameSize * 0.32 + NAME_GAP;
  if (name.syr_word) y += SYRIAC_ADVANCE;
  if (name.arb_word) y += ARABIC_ADVANCE;
  y += DIVIDER_GAP_BEFORE + DIVIDER_GAP_AFTER;
  y += meaningLines.length * MEANING_LINE_HEIGHT;
  y += FOOTER_GAP;

  const panelH = Math.max(MIN_CARD_HEIGHT - margin * 2, y + FOOTER_TO_BOTTOM);
  const cardHeight = panelH + margin * 2;
  canvas.width = CARD_WIDTH;
  canvas.height = cardHeight;

  // Soft, neutral wash behind the card panel.
  const bgGradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, cardHeight);
  bgGradient.addColorStop(0, '#fdf4ff');
  bgGradient.addColorStop(1, '#fff1f2');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, CARD_WIDTH, cardHeight);

  // White card panel.
  const panelX = margin;
  const panelY = margin;
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = '#ffffff';
  roundedRectPath(ctx, panelX, panelY, panelW, panelH, 40);
  ctx.fill();
  ctx.restore();

  // Decorative skyline, anchored to the bottom of the panel.
  drawSkyline(ctx, panelX, panelY, panelW, panelH, getSkylineTheme(genderLabel));

  ctx.textAlign = 'center';
  let drawY = panelY + HEADER_TOP;

  // BET KANU logo.
  if (logo) {
    const logoW = (logo.width / logo.height) * LOGO_HEIGHT;
    ctx.drawImage(logo, centerX - logoW / 2, drawY, logoW, LOGO_HEIGHT);
    drawY += LOGO_HEIGHT + LOGO_GAP;
  }

  // Gender + category pills.
  drawY = drawPillRow(ctx, pills, centerX, drawY, {}) + PILL_GAP;

  // Name (large serif display).
  ctx.font = nameFontTemplate(nameSize);
  ctx.fillStyle = '#1e2937';
  ctx.fillText(engName, centerX, drawY);
  drawY += nameSize * 0.32 + NAME_GAP;

  // Syriac / Arabic scripts, stacked beneath the name.
  if (name.syr_word) {
    const syriacFont = SYRIAC_FONTS[syriacFontType] || SYRIAC_FONTS.E;
    ctx.font = `600 132px "${syriacFont}"`;
    ctx.direction = 'rtl';
    ctx.fillStyle = '#1e2937';
    ctx.fillText(name.syr_word, centerX, drawY + 110);
    ctx.direction = 'ltr';
    drawY += SYRIAC_ADVANCE;
  }
  if (name.arb_word) {
    ctx.font = `600 118px "${SANS_FONT}", Arial, sans-serif`;
    ctx.direction = 'rtl';
    ctx.fillStyle = '#475569';
    ctx.fillText(name.arb_word, centerX, drawY + 90);
    ctx.direction = 'ltr';
    drawY += ARABIC_ADVANCE;
  }

  drawY += DIVIDER_GAP_BEFORE;
  // Ornamental divider.
  ctx.strokeStyle = '#fbcfe8';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(panelX + 90, drawY);
  ctx.lineTo(centerX - 60, drawY);
  ctx.moveTo(centerX + 60, drawY);
  ctx.lineTo(panelX + panelW - 90, drawY);
  ctx.stroke();
  drawLeafOrnament(ctx, centerX, drawY, 24, '#f472b6');
  drawY += DIVIDER_GAP_AFTER;

  // Meaning.
  if (meaningLines.length) {
    ctx.fillStyle = '#475569';
    ctx.font = meaningFont;
    ctx.textAlign = 'center';
    drawY = drawLines(ctx, meaningLines, centerX, drawY, MEANING_LINE_HEIGHT);
  }

  // Footer.
  const footerY = drawY + FOOTER_GAP;
  ctx.fillStyle = '#94a3b8';
  ctx.font = `700 20px "${SANS_FONT}", Arial, sans-serif`;
  fillTextSpaced(ctx, 'EXPLORE MORE NAMES AT', centerX, footerY, 3);

  ctx.fillStyle = '#7c3aed';
  ctx.font = `800 40px "${SANS_FONT}", Arial, sans-serif`;
  ctx.fillText('BETKANU.COM/NAMES', centerX, footerY + 50);

  return canvas;
}

export function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}
